import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import "./Login.css";

const TIMEZONES = [
  "America/Sao_Paulo", "America/New_York", "America/Chicago",
  "America/Denver", "America/Los_Angeles", "Europe/London",
  "Europe/Berlin", "Asia/Tokyo", "Australia/Sydney",
];

export default function Login() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const [deliveryTime, setDeliveryTime] = useState("07:00");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState("");
  const [resendError, setResendError] = useState("");
  const { login, register } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setUnverified(false);
    setResendSuccess("");
    setResendError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/dashboard");
      } else {
        await register({ email, password, timezone, delivery_time: deliveryTime + ":00" });
        navigate("/onboarding");
      }
    } catch (err) {
      if (mode === "login" && err.status === 403) {
        setUnverified(true);
      } else if (mode === "register") {
        // Registration succeeded but auto-login failed (email not verified yet)
        // or registration itself returned a verification-required response
        if (err.status === 403) {
          setMode("verify");
        } else {
          setError(err.message);
        }
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendSuccess("");
    setResendError("");
    try {
      await api.resendVerification(email, password);
      setResendSuccess(t("auth.verificationSent") || "Verification email sent!");
      setResendCooldown(60);
    } catch (err) {
      setResendError(err.message);
    }
  };

  // Verify email screen after registration
  if (mode === "verify") {
    return (
      <div className="login-page">
        <div className="login-bg">
          <div className="login-gradient" />
        </div>

        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <Link to="/" className="login-brand">
                <img src="/logos/mark-black-background.jpg" alt="ViewsDay" className="login-logo" />
              </Link>
              <div className="verify-icon">&#9993;</div>
              <h1 className="login-title">{t("auth.verifyYourEmail")}</h1>
              <p
                className="login-subtitle verify-body"
                dangerouslySetInnerHTML={{ __html: t("auth.verifyEmailSent", { email }) }}
              />
            </div>

            <div className="verify-actions">
              {resendSuccess && <div className="login-success">{resendSuccess}</div>}
              {resendError && <div className="login-error">{resendError}</div>}

              <button
                type="button"
                className="btn-primary btn-full"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? t("auth.resendIn", { seconds: resendCooldown })
                  : t("auth.resendEmail")}
              </button>

              <button
                type="button"
                className="link-btn"
                onClick={() => { setMode("login"); setError(""); setUnverified(false); setResendSuccess(""); setResendError(""); }}
              >
                {t("auth.backToLogin")}
              </button>
            </div>
          </div>

          <div className="login-decoration">
            <div className="deco-line" />
            <div className="deco-text">{t("login.testimonial")}</div>
            <div className="deco-author">{t("login.testimonialAuthor")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-gradient" />
      </div>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <Link to="/" className="login-brand">
              <img src="/logos/mark-black-background.jpg" alt="ViewsDay" className="login-logo" />
            </Link>
            <h1 className="login-title">
              {mode === "login" ? t("login.welcomeBack") : t("login.createAccount")}
            </h1>
            <p className="login-subtitle">
              {mode === "login" ? t("login.signInSubtitle") : t("login.registerSubtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {error && <div className="login-error">{error}</div>}

            <div className="form-group">
              <label className="form-label">{t("login.email")}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="form-input" required />
            </div>

            <div className="form-group">
              <label className="form-label">{t("login.password")}</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="form-input" required minLength={6} />
              {mode === "login" && (
                <Link to="/forgot-password" className="forgot-password-link">
                  {t("auth.forgotPassword")}
                </Link>
              )}
            </div>

            {mode === "register" && (
              <>
                <div className="form-group">
                  <label className="form-label">{t("login.timezone")}</label>
                  <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="form-input">
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t("login.deliveryTime")}</label>
                  <input type="time" value={deliveryTime} onChange={(e) => setDeliveryTime(e.target.value)} className="form-input" />
                  <span className="form-hint">{t("login.deliveryHint")}</span>
                </div>
              </>
            )}

            <button type="submit" className="btn-primary btn-full" disabled={loading}>
              {loading ? <span className="btn-spinner" /> : mode === "login" ? t("login.signInBtn") : t("login.createAccountBtn")}
            </button>
          </form>

          {/* Unverified email banner for login mode */}
          {unverified && mode === "login" && (
            <div className="unverified-banner">
              <p className="unverified-message">{t("auth.emailNotVerified")}</p>
              {resendSuccess && <div className="login-success">{resendSuccess}</div>}
              {resendError && <div className="login-error">{resendError}</div>}
              <button
                type="button"
                className="btn-primary btn-full btn-resend"
                onClick={handleResendVerification}
                disabled={resendCooldown > 0}
              >
                {resendCooldown > 0
                  ? t("auth.resendIn", { seconds: resendCooldown })
                  : t("auth.resendVerification")}
              </button>
            </div>
          )}

          <div className="login-switch">
            {mode === "login" ? (
              <>
                {t("login.noAccount")}{" "}
                <button onClick={() => { setMode("register"); setError(""); setUnverified(false); }} className="link-btn">{t("login.signUp")}</button>
              </>
            ) : (
              <>
                {t("login.hasAccount")}{" "}
                <button onClick={() => { setMode("login"); setError(""); setUnverified(false); }} className="link-btn">{t("login.signInBtn")}</button>
              </>
            )}
          </div>
        </div>

        <div className="login-decoration">
          <div className="deco-line" />
          <div className="deco-text">{t("login.testimonial")}</div>
          <div className="deco-author">{t("login.testimonialAuthor")}</div>
        </div>
      </div>
    </div>
  );
}
